import { Close } from '@mui/icons-material';
import { Box, Button, Drawer, DrawerProps, IconButton, Typography } from '@mui/material';
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

interface DrawerFooterActions {
  label: string;
  variant: 'text' | 'outlined' | 'contained';
  onClick: () => void;
}
interface OpenDrawerProps {
  anchor: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  header?: string;
  footerActions?: DrawerFooterActions[];
  content: ReactNode;
}

/**
 * Context type defining available drawer operations.
 */
interface DrawerContextType {
  /**
   * Opens the drawer with the given configuration.
   * @param props Configuration options for opening the drawer.
   */
  openDrawer: (props: OpenDrawerProps) => void;
  /**
   * Closes the drawer if all registered `canClose` functions allow it.
   */
  closeDrawer: () => void;
  /**
   * Registers a function to be called when the drawer is closing.
   * @param fn Function to execute on close.
   */
  addOnClose: (fn: () => void) => void;
  /**
   * Adds a condition that determines whether the drawer can close.
   * @param fn A function returning `true` if closing is allowed, `false` otherwise.
   * @returns A cleanup function to remove the condition when no longer needed.
   */
  addCanClose: (fn: () => boolean) => () => void;
}

/**
 * Drawer context for managing a reusable side drawer.
 */
const DrawerContext = createContext<DrawerContextType>({
  openDrawer: () => {},
  closeDrawer: () => {},
  addOnClose: () => {},
  addCanClose: () => () => {},
});

/**
 * Hook to access the drawer context.
 * @returns Drawer context functions for opening, closing, and managing the drawer.
 */
export const useDrawer = () => useContext(DrawerContext);

interface DrawerProviderProps {
  children: ReactNode;
}

const DrawerProvider = ({ children }: DrawerProviderProps) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DrawerProps['anchor']>('left');
  const [content, setContent] = useState<ReactNode>(null);
  const [size, setSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [header, setHeader] = useState<string | undefined>(undefined);
  const [footerActions, setFooterActions] = useState<DrawerFooterActions[] | undefined>(undefined);
  const [onCloseFunctions, setOnCloseFunctions] = useState<(() => void)[]>([]);
  const [canCloseFunctions, setCanCloseFunctions] = useState<(() => boolean)[]>([]);

  const drawerWidths: Record<'sm' | 'md' | 'lg', string> = {
    sm: '30vw',
    md: '50vw',
    lg: '70vw',
  };

  const openDrawer = useCallback(
    ({ anchor, size = 'md', header, footerActions, content }: OpenDrawerProps) => {
      setAnchor(anchor);
      setSize(size);
      setHeader(header);
      setFooterActions(footerActions);
      setContent(content);
      setOpen(true);
    },
    []
  );

  const addOnClose = useCallback((fn: () => void) => {
    setOnCloseFunctions((prev) => [...prev, fn]);
  }, []);

  const addCanClose = useCallback((fn: () => boolean) => {
    setCanCloseFunctions((prev) => [...prev, fn]);

    return () => {
      setCanCloseFunctions((prev) => prev.filter((f) => f !== fn));
    };
  }, []);

  const closeDrawer = useCallback(() => {
    if (canCloseFunctions.some((fn) => !fn())) return;

    // Run all registered close functions
    onCloseFunctions.forEach((fn) => fn());

    setOpen(false);
    setHeader(undefined);
    setContent(null);
    setOnCloseFunctions([]);
    setCanCloseFunctions([]);
  }, [onCloseFunctions, canCloseFunctions]);

  return (
    <DrawerContext.Provider
      value={{
        openDrawer,
        closeDrawer,
        addOnClose,
        addCanClose,
      }}
    >
      {children}
      <Drawer anchor={anchor} open={open} onClose={closeDrawer}>
        {header && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom="1px solid #ccc"
            gap={2}
            p={2}
            width={drawerWidths[size]}
            minWidth={'450px'}
          >
            <Typography
              variant="h1"
              sx={{ fontSize: '20px', fontWeight: '600', lineHeight: '30px' }}
            >
              {header}
            </Typography>
            <IconButton onClick={closeDrawer}>
              <Close />
            </IconButton>
          </Box>
        )}
        <Box width={drawerWidths[size]} minWidth={'450px'}>
          {content}
        </Box>
        {footerActions?.length && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              width: '100%',
              height: '60px',
              borderTop: '1px solid #ddd',
              gap: 3,
            }}
          >
            {footerActions.map(({ label, variant, onClick }) => (
              <Button key={label} variant={variant} onClick={onClick}>
                {label}
              </Button>
            ))}
          </Box>
        )}
      </Drawer>
    </DrawerContext.Provider>
  );
};

export default DrawerProvider;
